/**
 * The Ynex template's theme state, without Redux.
 *
 * The template keeps a Redux store whose fields are written onto <html> as attributes,
 * and its stylesheet keys the whole layout off them. This module holds the same fields
 * with the same defaults and applies them the same way. Only what OHC's staff use is
 * kept: the vertical layout with the sidebar that folds to icons, and light and dark.
 * The template's customisation panel is for people buying the template, not for staff at
 * a till, so it is left out.
 *
 * The rules for folding the sidebar are the template's own, from its header component:
 *   - on a wide screen the sidebar folds to icons and opens again on hover
 *   - under 992px it is hidden, and slides over the page with a dimmed overlay
 */

import { useSyncExternalStore } from "react";

export interface ThemeState {
  /** "light" or "dark", written as the class on <html>. */
  readonly mode: "light" | "dark";
  readonly dataHeaderStyles: "light" | "dark";
  readonly dataMenuStyles: "light" | "dark";
  /** "" open, "icon-overlay-close" folded to icons, "close" and "open" on a phone. */
  readonly dataToggled: string;
  /** "open" while the pointer is over a folded sidebar. */
  readonly iconOverlay: string;
}

/** The template breakpoint where the sidebar stops being a column and becomes a drawer. */
export const DRAWER_BREAKPOINT = 992;

const STORAGE_KEY = "ohc.theme";

function initial(): ThemeState {
  let mode: ThemeState["mode"] = "light";
  try {
    if (localStorage.getItem(STORAGE_KEY) === "dark") mode = "dark";
  } catch {
    // Private window or blocked storage: light, as the template starts.
  }
  const narrow = typeof window !== "undefined" && window.innerWidth < DRAWER_BREAKPOINT;
  return {
    mode,
    dataHeaderStyles: mode,
    // The template keeps the sidebar dark in both modes on the vertical layout.
    dataMenuStyles: "dark",
    dataToggled: narrow ? "close" : "",
    iconOverlay: "",
  };
}

let state: ThemeState = initial();
const listeners = new Set<() => void>();

function apply(): void {
  const html = document.documentElement;
  // The same attributes, with the same values, that the template's switcher writes.
  html.setAttribute("class", state.mode);
  html.setAttribute("dir", "ltr");
  html.setAttribute("data-nav-layout", "vertical");
  html.setAttribute("data-vertical-style", "overlay");
  html.setAttribute("data-header-styles", state.dataHeaderStyles);
  html.setAttribute("data-menu-styles", state.dataMenuStyles);
  html.setAttribute("data-toggled", state.dataToggled);
  html.setAttribute("icon-overlay", state.iconOverlay);
  html.setAttribute("data-page-style", "regular");
  html.setAttribute("data-width", "fullwidth");
  html.setAttribute("data-menu-position", "fixed");
  html.setAttribute("data-header-position", "fixed");
}

function set(change: Partial<ThemeState>): void {
  state = { ...state, ...change };
  apply();
  for (const listener of listeners) listener();
}

export function useTheme(): ThemeState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

/** The template's header toggle, for its default "overlay" vertical style. */
export function toggleSidebar(): void {
  if (window.innerWidth >= DRAWER_BREAKPOINT) {
    set({
      dataToggled: state.dataToggled === "icon-overlay-close" ? "" : "icon-overlay-close",
      iconOverlay: "",
    });
    return;
  }

  if (state.dataToggled === "close") {
    set({ dataToggled: "open" });
    document.querySelector("#responsive-overlay")?.classList.add("active");
  } else {
    closeDrawer();
  }
}

/** On a phone: put the drawer away. Called by the overlay and after choosing a page. */
export function closeDrawer(): void {
  if (window.innerWidth < DRAWER_BREAKPOINT) set({ dataToggled: "close" });
  document.querySelector("#responsive-overlay")?.classList.remove("active");
}

/** A folded sidebar opens over the page while the pointer is on it, as the template does. */
export function sidebarHover(over: boolean): void {
  if (state.dataToggled !== "icon-overlay-close") return;
  if (over && state.iconOverlay !== "open") set({ iconOverlay: "open" });
  if (!over && state.iconOverlay === "open") set({ iconOverlay: "" });
}

/** The template's dark mode switch: page, header and sidebar move together. */
export function toggleDark(): void {
  const mode = state.mode === "dark" ? "light" : "dark";
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // Not remembered, still applied.
  }
  set({ mode, dataHeaderStyles: mode, dataMenuStyles: "dark" });
}

let previousWidth = typeof window !== "undefined" ? window.innerWidth : 1200;

/** Crossing the breakpoint resets the sidebar, the same as the template's resize handler. */
function onResize(): void {
  const width = window.innerWidth;
  if (width < DRAWER_BREAKPOINT && previousWidth >= DRAWER_BREAKPOINT) {
    set({ dataToggled: "close", iconOverlay: "" });
  }
  if (width >= DRAWER_BREAKPOINT && previousWidth < DRAWER_BREAKPOINT) {
    set({ dataToggled: "", iconOverlay: "" });
    document.querySelector("#responsive-overlay")?.classList.remove("active");
  }
  previousWidth = width;
}

export function startTheme(): void {
  apply();
  window.addEventListener("resize", onResize);
}
